import { useEffect, useRef, useState } from "react";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtTime } from "@/components/Pager";
import { MapPin, SignalZero, Radio } from "lucide-react";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
let keyRejected = false;
const keyListeners = new Set();
window.gm_authFailure = () => { keyRejected = true; keyListeners.forEach((f) => f(true)); };
function useKeyRejected() {
  const [bad, setBad] = useState(keyRejected);
  useEffect(() => { keyListeners.add(setBad); return () => { keyListeners.delete(setBad); }; }, []);
  return bad;
}
export const STALE_MS = 2 * 60 * 1000;
export const MANAGER_ROLES = ["master_admin", "super_admin", "principal"];
export const STATUS = { picked_up: ["Picked up", "bg-emerald-100 text-emerald-800"], absent: ["Absent", "bg-rose-100 text-rose-700"], dropped_off: ["Dropped off", "bg-sky-100 text-sky-800"] };
export const card = "bg-white rounded-2xl shadow-sm p-5";

export function usePoll(fn, ms, deps = []) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    ref.current();
    const t = setInterval(() => ref.current(), ms);
    return () => clearInterval(t);
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useBranch() {
  const { user } = useAuth();
  const global = ["master_admin", "super_admin"].includes(user?.role);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id || "");
  useEffect(() => {
    api.get("/branches").then((r) => { setBranches(r.data); if (!branchId && r.data[0]) setBranchId(r.data[0].id); }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { branches, branchId, setBranchId, global };
}

export function BranchSelect({ b }) {
  if (!b.global) return null;
  return (
    <Select value={b.branchId} onValueChange={b.setBranchId}>
      <SelectTrigger data-testid="transport-branch" className="w-64 bg-white"><SelectValue placeholder="Branch" /></SelectTrigger>
      <SelectContent>{b.branches.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function LocationNote({ loc, testid = "location-note" }) {
  if (!loc) return <div data-testid={testid} className="flex items-center gap-2 text-sm text-slate-500"><SignalZero size={15} /> No GPS location received yet.</div>;
  const stale = Date.now() - new Date(loc.at).getTime() > STALE_MS;
  return stale ? (
    <div data-testid={testid} className="flex items-center gap-2 text-sm text-amber-700"><SignalZero size={15} /> GPS unavailable — last known location at {fmtTime(loc.at)}</div>
  ) : (
    <div data-testid={testid} className="flex items-center gap-2 text-sm text-emerald-700"><Radio size={15} className="animate-pulse" /> Live • updated {fmtTime(loc.at)} via {loc.source === "tracker" ? "GPS tracker" : "staff phone"}</div>
  );
}

const gmaps = (p) => `https://www.google.com/maps?q=${p.lat},${p.lng}`;

// buses: [{id, label, loc}] ; stops: [{id, name, lat, lng}]
export function BusMap({ buses = [], stops = [], height = 320, testid = "bus-map" }) {
  const pts = stops.filter((s) => s.lat != null && s.lng != null);
  const withLoc = buses.filter((b) => b.loc);
  const rejected = useKeyRejected();
  if (!KEY || rejected) {
    return (
      <div data-testid={testid} className="rounded-xl border border-dashed border-violet-200 bg-violet-50/50 p-4 text-sm text-slate-600 space-y-1">
        <div className="font-semibold text-violet-800 flex items-center gap-2"><MapPin size={15} /> Map not connected yet</div>
        <div>{rejected ? "The Google Maps key was not accepted, so the map can't load." : "The live map appears once the Google Maps key is added."}</div>
        {withLoc.map((b) => <div key={b.id}>{b.label}: <a className="text-violet-700 underline" href={gmaps(b.loc)} target="_blank" rel="noreferrer" data-testid={`${testid}-open-${b.id}`}>open last location in Google Maps</a></div>)}
      </div>
    );
  }
  const first = withLoc[0]?.loc || pts[0] || { lat: 17.385, lng: 78.4867 };
  return (
    <div data-testid={testid} className="rounded-xl overflow-hidden" style={{ height }}>
      <APIProvider apiKey={KEY}>
        <Map key={`${withLoc[0]?.id || "none"}-${pts.length}`} defaultCenter={{ lat: first.lat, lng: first.lng }} defaultZoom={withLoc.length || pts.length ? 13 : 5}
          gestureHandling="greedy" streetViewControl={false} mapTypeControl={false} fullscreenControl style={{ width: "100%", height: "100%" }}>
          {pts.map((s, i) => <Marker key={s.id} position={{ lat: s.lat, lng: s.lng }} title={s.name} label={String(i + 1)} />)}
          {withLoc.map((b) => <Marker key={b.id} position={{ lat: b.loc.lat, lng: b.loc.lng }} title={`${b.label} • ${fmtTime(b.loc.at)}`}
            icon={{ url: "https://maps.google.com/mapfiles/kml/shapes/bus.png", scaledSize: window.google ? new window.google.maps.Size(36, 36) : undefined }} />)}
        </Map>
      </APIProvider>
    </div>
  );
}

export function downloadCsv(name, rows) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const blob = new Blob([[keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
}
